/**
 * DocumentUpload Component
 *
 * File upload with drag-and-drop support.
 * Uses generateSignedUrl to get upload URL, then uploads to Cloud Storage.
 */

import { useState, useRef, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { Upload, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

interface DocumentUploadProps {
  caseFileId: string;
  onUploadComplete?: (documentId: string) => void;
  onUploadError?: (error: string) => void;
}

interface UploadingFile {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  documentId?: string;
}

interface SignedUrlResponse {
  signedUrl: string;
  documentId: string;
  filePath: string;
  expiresAt: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/tiff',
  'image/webp',
  'image/heic',
  'image/bmp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/rtf',
  'text/plain',
  'text/csv',
];

export function DocumentUpload({
  caseFileId,
  onUploadComplete,
  onUploadError,
}: DocumentUploadProps): JSX.Element {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'File type not supported. Please upload PDF, images, or documents.';
    }
    return null;
  };

  const uploadFile = async (file: File): Promise<void> => {
    const uploadId = `${Date.now()}-${file.name}`;

    // Add to uploading list
    setUploadingFiles((prev) => [
      ...prev,
      { id: uploadId, file, progress: 0, status: 'pending' },
    ]);

    // Validate
    const validationError = validateFile(file);
    if (validationError) {
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.id === uploadId ? { ...f, status: 'error', error: validationError } : f
        )
      );
      onUploadError?.(validationError);
      return;
    }

    try {
      // Update status to uploading
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.id === uploadId ? { ...f, status: 'uploading', progress: 10 } : f
        )
      );

      // Get signed URL
      const getSignedUrl = httpsCallable<
        { caseFileId: string; fileName: string; contentType: string },
        SignedUrlResponse
      >(functions, 'generateSignedUrl');

      const result = await getSignedUrl({
        caseFileId,
        fileName: file.name,
        contentType: file.type,
      });

      if (!result.data.signedUrl) {
        throw new Error('Failed to get upload URL');
      }

      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.id === uploadId ? { ...f, progress: 30 } : f
        )
      );

      // Upload file to signed URL
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = 30 + Math.round((event.loaded / event.total) * 60);
          setUploadingFiles((prev) =>
            prev.map((f) =>
              f.id === uploadId ? { ...f, progress } : f
            )
          );
        }
      };

      await new Promise<void>((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Upload failed'));

        xhr.open('PUT', result.data.signedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      // Success
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.id === uploadId
            ? { ...f, status: 'success', progress: 100, documentId: result.data.filePath }
            : f
        )
      );

      onUploadComplete?.(result.data.filePath);
    } catch (err) {
      console.error('Upload error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';

      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.id === uploadId ? { ...f, status: 'error', error: errorMessage } : f
        )
      );

      onUploadError?.(errorMessage);
    }
  };

  const handleFiles = useCallback((files: FileList | File[]) => {
    Array.from(files).forEach((file) => {
      uploadFile(file);
    });
  }, [caseFileId]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      e.target.value = ''; // Reset input
    }
  };

  const removeFile = (id: string) => {
    setUploadingFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const retryFile = (file: UploadingFile) => {
    removeFile(file.id);
    uploadFile(file.file);
  };

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-evergreen bg-evergreen/5'
            : 'border-soft-neutral hover:border-slate-ink/50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_TYPES.join(',')}
          onChange={handleFileSelect}
          className="hidden"
        />
        <Upload
          size={40}
          className={`mx-auto mb-3 ${isDragging ? 'text-evergreen' : 'text-muted'}`}
        />
        <p className="text-ink font-medium mb-1">
          {isDragging ? 'Drop files here' : 'Drop files here or click to browse'}
        </p>
        <p className="text-sm text-muted">
          PDF, images, Word docs up to 10MB
        </p>
      </div>

      {/* Uploading files list */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          {uploadingFiles.map((uploadFile) => (
            <div
              key={uploadFile.id}
              className="flex items-center gap-3 p-3 bg-soft-neutral/50 rounded-lg"
            >
              {uploadFile.status === 'uploading' && (
                <Loader2 size={20} className="text-evergreen animate-spin" />
              )}
              {uploadFile.status === 'success' && (
                <CheckCircle size={20} className="text-evergreen" />
              )}
              {uploadFile.status === 'error' && (
                <AlertCircle size={20} className="text-red-500" />
              )}
              {uploadFile.status === 'pending' && (
                <div className="w-5 h-5 border-2 border-muted rounded-full" />
              )}

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink truncate">
                  {uploadFile.file.name}
                </p>
                {uploadFile.status === 'uploading' && (
                  <div className="mt-1 h-1.5 bg-soft-neutral rounded-full overflow-hidden">
                    <div
                      className="h-full bg-evergreen transition-all duration-300"
                      style={{ width: `${uploadFile.progress}%` }}
                    />
                  </div>
                )}
                {uploadFile.status === 'error' && (
                  <p className="text-xs text-red-500 mt-0.5">{uploadFile.error}</p>
                )}
              </div>

              {uploadFile.status === 'error' && (
                <button
                  onClick={() => retryFile(uploadFile)}
                  className="text-sm text-evergreen hover:text-deep-forest"
                >
                  Retry
                </button>
              )}

              <button
                onClick={() => removeFile(uploadFile.id)}
                className="text-muted hover:text-ink"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DocumentUpload;
